import { useContext, useMemo } from 'react';
import MantineTable from '../../../../../common/table/mantine';
import { Button, Checkbox, NumberInput, Text } from '@mantine/core';
import { Status } from '../../../styles';
import { CostingRequestsContext } from '../../../../../contexts/CostingRequest';
import { useSearchParams } from 'react-router-dom';
import AssignedUsersList from '../../../../../components/assignedUsersList';
const VLCTable = ({
  handleRefresh,
  data,
  handleCostChange,
  loading,
  setLogsModal,
  logsHeader,
  csvFilename,
  submitCostChange,
  setData,
  checkActive,
  checkActiveFpna,
  fetch,
}) => {
  const { startManualClosure, info, isCrRoleAuthorized } = useContext(CostingRequestsContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSubTab = searchParams.get('subtab');
  const isNotTP = info.type_of_request !== 'Transfer Pricing';

  const columns = useMemo(() => {
    const baseColumns = [];

    // Conditionally add Retry column
    if (isNotTP) {
      baseColumns.push({
        accessorKey: 'logs',
        header: 'Action',
        Cell: ({ row }) => {
          const status = row.original?.status;
          const country = row.original?.producing_country;
          const value = row?.original?.costPerHL;
          const access = checkActiveFpna(status, country, value);

          return (
            <Button
              disabled={!access}
              variant='subtle'
              color='dark'
              size='sm'
              style={{ width: '100%' }}
              onClick={() => handleRefresh(row.original?.id, row?.original?.sales_org)}
            >
              Retry
            </Button>
          );
        },
      });
    }

    // Standard columns
    baseColumns.push(
      {
        id: 'sku',
        header: 'SKU No',
        Cell: ({ row }) => <div>{row.original?.sku}</div>,
      },
      {
        id: 'sku_desc',
        header: 'SKU Description',
        Cell: ({ row }) => <div>{row.original?.sku_desc}</div>,
      },
      {
        accessorKey: 'sales_org',
        header: 'Sales Org',
      },
      {
        accessorKey: 'distribution_channel',
        header: 'Distribution Channel',
      },
      {
        accessorKey: 'condition_type',
        header: 'Condn Type',
      },
      {
        accessorKey: 'key_combination',
        header: 'Key Combn',
      },
      {
        accessorKey: 'costPerHL',
        header: 'Cost/HL',
        Cell: ({ row }) => {
          const status = row.original?.status;
          const country = row.original?.producing_country;
          const access = checkActive(status, country, 'logistics');

          return isNotTP ? (
            <NumberInput
              disabled={!access}
              min={0}
              max={1000000}
              precision={4}
              value={parseFloat(row.original?.costPerHL || 0) || 0}
              onChange={(val) => handleCostChange(val, row?.original?.id)}
            />
          ) : (
            <Text>{row.original?.costPerHL}</Text>
          );
        },
      },
      {
        accessorKey: 'costPerHundred',
        header: 'Cost/100HL',
      },
      {
        accessorKey: 'currency',
        header: 'Curr',
      },
      {
        accessorKey: 'unit',
        header: 'Unit',
      },
      {
        accessorKey: 'uom',
        header: 'Uom',
      },
      {
        accessorKey: 'valid_from',
        header: 'Valid From',
      },
      {
        accessorKey: 'valid_to',
        header: 'Valid To',
      },
      {
        id: 'status',
        header: 'Status',
        Cell: ({ row }) =>
          row.original?.status ? (
            <Status
              status={
                ['closed', 'completed'].includes(String(row.original?.status).toLowerCase())
                  ? 'Completed'
                  : row.original?.status
              }
            />
          ) : (
            'N/A'
          ),
      },
    );

    // Conditionally add Manual Closure column
    if (isNotTP) {
      baseColumns.push({
        header: 'Action',
        id: 'manually_closed',
        disableSortBy: true,
        Cell: ({ row }) => {
          const access = isCrRoleAuthorized(info.producing_plant_data[0].producing_plant);

          return ['In Progress', 'Not Started'].includes(row.original.status) ? (
            <Checkbox
              onChange={() =>
                startManualClosure({ open: true, id: row.original.id, callback: fetch })
              }
              defaultChecked={false}
              label='Confirm and Close'
              disabled={['Closed', 'Not Started'].includes(row.original.status) || !access}
            />
          ) : row.original.manually_closed ? (
            <Text>Manual Closure</Text>
          ) : (
            <Text>Automatic Closure</Text>
          );
        },
      });
    }

    // Logs column
    baseColumns.push({
      accessorKey: 'logs1',
      header: 'Logs',
      Cell: ({ row }) => (
        <Button
          color='dark'
          onClick={() =>
            setLogsModal({
              open: true,
              logs: row.original?.IntCrStepStatusLogs,
              type: logsHeader,
            })
          }
        >
          View Logs
        </Button>
      ),
    });

    // Conditionally add Submitted On column
    if (isNotTP) {
      baseColumns.push({
        accessorKey: 'submittedOn',
        header: 'Submitted On',
      });
    }

    // Always shown
    baseColumns.push(
      {
        accessorKey: 'completedOn',
        header: 'Completed On',
      },
      {
        accessorKey: 'logisticsFpna',
        header: 'Logistics FP&A SME',
        Cell: ({
          row: {
            original: { logisticsFpna },
          },
        }) => <AssignedUsersList users={logisticsFpna} />,
      },
      {
        accessorKey: 'fpna_gcc_sme',
        header: 'FP&A GCC SME',
        Cell: ({
          row: {
            original: { fpna_gcc_sme },
          },
        }) => <AssignedUsersList users={fpna_gcc_sme} />,
      },
    );

    // Conditionally add Submit column
    if (isNotTP) {
      baseColumns.push({
        accessorKey: 'action',
        header: 'Submit',
        Cell: ({ row }) => {
          const status = row.original?.status;
          const country = row.original?.producing_country;
          const access = checkActive(status, country, 'logistics');

          return (
            <Button
              disabled={!access}
              color='dark'
              onClick={() =>
                submitCostChange(
                  row.original?.costPerHL,
                  row?.original?.id,
                  row.original?.sales_org,
                )
              }
            >
              Submit
            </Button>
          );
        },
      });
    }

    return baseColumns;
  }, [handleRefresh, setData, handleCostChange, data, isNotTP]);

  const filteredColumns = useMemo(() => {
    return activeSubTab === 'fobSetup'
      ? columns.filter((col) => col.id !== 'manually_closed')
      : columns;
  }, [columns, activeSubTab]);

  const initialState = {
    columnPinning: {
      left: ['logs'],
      right: ['action'],
    },
    sorting: [
      {
        id: 'sku_description',
        desc: false,
      },
    ],
  };
  return (
    <>
      <MantineTable
        columns={filteredColumns}
        initialData={data}
        enableRowSelection={false}
        loading={loading}
        showSelectedToggle={false}
        hideSelectColumn={true}
        showResetAll={false}
        pageSize={10}
        initialState={initialState}
        enableColumnFilter={false}
        enableFilters={false}
        enableExport={true}
        columnFilterDisplayMode={false}
        csvFilename={csvFilename}
      />
    </>
  );
};

export default VLCTable;
